import { useState, useCallback, useRef, useEffect } from "react";
import {
  useGenerateJobUploadUrlAsUser,
  useCheckDuplicateFileAsUser,
} from "@taxassist/api-client";
import { useCurrentFirmId } from "../contexts/FirmContext";
import axios from "axios";
import SparkMD5 from "spark-md5";

export type FileUploadStatus =
  | "queued"
  | "uploading"
  | "completed"
  | "failed"
  | "duplicate";

export interface FileUploadItem {
  id: string;
  file: File;
  status: FileUploadStatus;
  progress: number;
  error?: string;
  s3Key?: string;
  uploadUrl?: string;
  md5Hash?: string;
}

interface UseFileUploadOptions {
  jobId: string;
  maxConcurrentUploads?: number;
  onUploadComplete?: (files: FileUploadItem[]) => void;
  onError?: (error: Error) => void;
}

export function useFileUpload({
  jobId,
  maxConcurrentUploads = 2,
  onUploadComplete,
  onError,
}: UseFileUploadOptions) {
  const [uploadQueue, setUploadQueue] = useState<FileUploadItem[]>([]);
  const [activeUploads, setActiveUploads] = useState<FileUploadItem[]>([]);
  const [completedUploads, setCompletedUploads] = useState<FileUploadItem[]>(
    [],
  );
  const [failedUploads, setFailedUploads] = useState<FileUploadItem[]>([]);
  const [duplicateUploads, setDuplicateUploads] = useState<FileUploadItem[]>(
    [],
  );

  const uploadInProgressRef = useRef(false);
  const generateUploadUrl = useGenerateJobUploadUrlAsUser();
  const checkDuplicateFile = useCheckDuplicateFileAsUser();
  const firmId = useCurrentFirmId();

  // Add files to the queue
  const addFiles = useCallback((files: File[]) => {
    const newItems: FileUploadItem[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      status: "queued" as FileUploadStatus,
      progress: 0,
    }));

    setUploadQueue((prev) => [...prev, ...newItems]);
  }, []);

  // Remove a file from the queue or duplicates
  const removeFile = useCallback((fileId: string) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== fileId));
    setDuplicateUploads((prev) => prev.filter((item) => item.id !== fileId));
  }, []);

  // Retry a failed upload
  const retryFile = useCallback((fileId: string) => {
    setFailedUploads((prev) => {
      const file = prev.find((item) => item.id === fileId);
      if (file) {
        const retriedFile = {
          ...file,
          status: "queued" as FileUploadStatus,
          progress: 0,
          error: undefined,
        };
        setUploadQueue((queue) => [...queue, retriedFile]);
        return prev.filter((item) => item.id !== fileId);
      }
      return prev;
    });
  }, []);

  // Clear completed uploads
  const clearCompleted = useCallback(() => {
    setCompletedUploads([]);
  }, []);

  const calculateMD5 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      const spark = new SparkMD5.ArrayBuffer();
      fileReader.onload = (e) => {
        spark.append(e.target?.result as ArrayBuffer);
        resolve(spark.end());
      };
      fileReader.onerror = (e) => {
        reject(e);
      };
      fileReader.readAsArrayBuffer(file);
    });
  };

  // Upload a single file
  const uploadFile = async (item: FileUploadItem): Promise<FileUploadItem> => {
    try {
      // Check if firmId is available
      if (!firmId) {
        throw new Error(
          "Firm ID not available. Please ensure you are logged in and a firm is selected.",
        );
      }

      // Update status to uploading
      const uploadingItem = {
        ...item,
        status: "uploading" as FileUploadStatus,
      };

      const md5Hash = await calculateMD5(item.file);
      uploadingItem.md5Hash = md5Hash;

      const duplicateCheck = await checkDuplicateFile.mutateAsync({
        firmId,
        jobId,
        data: { md5Hash },
      });

      if (duplicateCheck.isDuplicate) {
        return {
          ...uploadingItem,
          status: "duplicate",
          error: `Duplicate of ${duplicateCheck.existingFile?.fileName}`,
        };
      }

      // Generate presigned URL
      const urlResponse = await generateUploadUrl.mutateAsync({
        firmId,
        jobId,
        data: {
          fileName: item.file.name,
          contentType: item.file.type || "application/octet-stream",
          fileSize: item.file.size,
          md5Hash,
        },
      });

      uploadingItem.uploadUrl = urlResponse.uploadUrl;
      uploadingItem.s3Key = urlResponse.s3Key;

      // Upload to S3 using presigned URL
      await axios.put(urlResponse.uploadUrl, item.file, {
        headers: {
          "Content-Type": item.file.type || "application/octet-stream",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setActiveUploads((prev) =>
              prev.map((upload) =>
                upload.id === item.id ? { ...upload, progress } : upload,
              ),
            );
          }
        },
      });

      // Mark as completed
      return {
        ...uploadingItem,
        status: "completed",
        progress: 100,
      };
    } catch (error) {
      console.error("Upload failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";

      return {
        ...item,
        status: "failed",
        error: errorMessage,
      };
    }
  };

  // Process the upload queue
  const processQueue = useCallback(async () => {
    if (uploadInProgressRef.current || uploadQueue.length === 0) {
      return;
    }

    const availableSlots = maxConcurrentUploads - activeUploads.length;
    if (availableSlots <= 0) {
      return;
    }

    uploadInProgressRef.current = true;

    // Take items from queue up to available slots
    const itemsToUpload = uploadQueue.slice(0, availableSlots);
    const remainingQueue = uploadQueue.slice(availableSlots);

    // Move items from queue to active
    setUploadQueue(remainingQueue);
    setActiveUploads((prev) => [...prev, ...itemsToUpload]);

    // Upload files concurrently
    const uploadPromises = itemsToUpload.map(async (item) => {
      const result = await uploadFile(item);

      // Remove from active uploads
      setActiveUploads((prev) =>
        prev.filter((upload) => upload.id !== item.id),
      );

      // Add to completed, failed, or duplicate
      if (result.status === "completed") {
        setCompletedUploads((prev) => [...prev, result]);
      } else if (result.status === "failed") {
        setFailedUploads((prev) => [...prev, result]);
      } else if (result.status === "duplicate") {
        setDuplicateUploads((prev) => [...prev, result]);
      }

      return result;
    });

    try {
      const results = await Promise.all(uploadPromises);

      // Notify completion
      const completed = results.filter((r) => r.status === "completed");
      if (completed.length > 0) {
        onUploadComplete?.(completed);
      }

      // Notify errors
      const failed = results.filter((r) => r.status === "failed");
      failed.forEach((f) => {
        if (f.error) {
          onError?.(new Error(f.error));
        }
      });

      // Notify duplicates
      const duplicates = results.filter((r) => r.status === "duplicate");
      duplicates.forEach((d) => {
        if (d.error) {
          onError?.(new Error(d.error));
        }
      });
    } catch (error) {
      console.error("Error processing upload queue:", error);
      if (error instanceof Error) {
        onError?.(error);
      }
    } finally {
      uploadInProgressRef.current = false;

      // Process next batch if there are more items
      if (remainingQueue.length > 0) {
        setTimeout(processQueue, 100);
      }
    }
  }, [
    uploadQueue,
    activeUploads.length,
    maxConcurrentUploads,
    uploadFile,
    onUploadComplete,
    onError,
    jobId,
    firmId,
    generateUploadUrl,
  ]);

  // Trigger queue processing when items are added
  useEffect(() => {
    if (uploadQueue.length > 0 && activeUploads.length < maxConcurrentUploads) {
      processQueue();
    }
  }, [
    uploadQueue.length,
    activeUploads.length,
    maxConcurrentUploads,
    processQueue,
  ]);

  return {
    uploadQueue,
    activeUploads,
    completedUploads,
    failedUploads,
    duplicateUploads,
    addFiles,
    removeFile,
    retryFile,
    clearCompleted,
    isUploading: activeUploads.length > 0,
    totalFiles:
      uploadQueue.length +
      activeUploads.length +
      completedUploads.length +
      failedUploads.length +
      duplicateUploads.length,
  };
}
